// src/evaluations/potentialReport/PotentialReportView.js
// Pestaña "Informe de potencial" del resultado del assessment
// (PLAN_UNIFICACION_ASSESSMENT.md §7). Muestra las mismas hojas que el PDF
// (carta horizontal, 1056 x 816 px), escaladas al ancho disponible.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';
import PotentialReportPage from './PotentialReportPages';
import { POTENTIAL_REPORT_CSS } from './potentialReportStyles';

const PAGE_WIDTH = 1056;
const PAGE_HEIGHT = 816;
const GAP = 16;

export default function PotentialReportView({ responseId }) {
    const { t } = useTranslation();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [scale, setScale] = useState(1);
    const containerRef = useRef(null);

    const loadReport = useCallback(async () => {
        if (!responseId) return;
        setLoading(true);
        setError(false);
        try {
            const { authToken } = useStore.getState();
            const response = await api.get(
                `/evaluation-responses/${responseId}/potential-report`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (response.data?.errorCode === 0 && response.data?.response?.status_response === 'OK') {
                setReport(response.data.response.data);
            } else {
                setError(true);
            }
        } catch (err) {
            console.error('Error loading potential report:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [responseId]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // Escala las hojas al ancho del contenedor (nunca más grandes que el PDF)
    useLayoutEffect(() => {
        const node = containerRef.current;
        if (!node) return undefined;
        const update = () => {
            const available = node.clientWidth - 2 * GAP;
            if (available > 0) setScale(Math.min(1, available / PAGE_WIDTH));
        };
        update();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    let content;
    if (loading) {
        content = (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 8 }}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">{t('evaluationResponses.potentialReportLoading')}</Typography>
            </Box>
        );
    } else if (error || !report) {
        content = (
            <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={loadReport}>{t('retry')}</Button>}
            >
                {t('evaluationResponses.potentialReportError')}
            </Alert>
        );
    } else {
        content = (
            <div className="pr-root">
                <style>{POTENTIAL_REPORT_CSS}</style>
                {report.pages.map((page) => (
                    <div
                        key={page.key}
                        style={{
                            width: PAGE_WIDTH * scale,
                            height: PAGE_HEIGHT * scale,
                            margin: `0 auto ${GAP}px`,
                            overflow: 'hidden',
                            background: '#ffffff',
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.18)',
                        }}
                    >
                        <div style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                            <PotentialReportPage report={report} page={page} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <Box ref={containerRef} sx={{ bgcolor: '#eef0f3', p: `${GAP}px`, minHeight: '100%' }}>
            {content}
        </Box>
    );
}
